import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, Output, SimpleChanges } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { FilePreviewComponent, TileComponent } from '@tecalisdev/infinity';
import { IProcessDetailResponse } from '../../models/processes.models';
import { firstOcrBackPreviewUrl, firstOcrFrontPreviewUrl } from '../../utils/ocr-admin-images.util';
import { RolePermissionsService } from '../../../core/services/role-permissions.service';
import { ProcessHelperService } from '../../services/process-helper.service';

@Component({
	selector: 'app-process-detail-media-previews',
	imports: [CommonModule, TranslatePipe, FilePreviewComponent, TileComponent],
	templateUrl: './process-detail-media-previews.component.html',
	styleUrl: './process-detail-media-previews.component.scss',
})
export class ProcessDetailMediaPreviewsComponent {
	@Input({ required: true }) detail!: IProcessDetailResponse;
	/** `true` mientras el API aún no ha devuelto URLs de vídeo/imágenes. */
	@Input() loading = false;

	@Output() readonly mediaPreviewClick = new EventEmitter<string>();

	private rolePermissions = inject(RolePermissionsService);
	private processHelper = inject(ProcessHelperService);

	public gridTemplateColumns: string = '1fr 1fr';
	private readonly oneFractionWidth = 151;
	public gridMaxWidth: string = `${this.oneFractionWidth * 2}px`;

	/** Selfies POL a mostrar (estática: solo la oficial; activa: igual que antes, hasta 2). */
	public get polSelfiePreviews(): string[] {
		return this.processHelper.polSelfiesForAdminPreview(
			this.detail?.data?.POL?.images?.selfie,
			this.detail?.data?.POL?.mode,
		);
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (changes['detail']) {
			this.updateConfigGrid();
		}
	}

	get ocrFrontPreviewUrl(): string | undefined {
		return firstOcrFrontPreviewUrl(this.detail?.data?.OCR?.images);
	}

	get ocrBackPreviewUrl(): string | undefined {
		return firstOcrBackPreviewUrl(this.detail?.data?.OCR?.images);
	}

	get showLoadingState(): boolean {
		if (!this.loading) {
			return false;
		}
		return !!(
			this.detail?.data?.OCR ||
			this.detail?.data?.POL ||
			this.detail?.data?.DOCUMENTS ||
			this.detail?.general?.video?.trim()
		);
	}

	get hasVisibleMedia(): boolean {
		return !!(
			(this.detail?.general?.video && this.detail.general.video.trim() !== '') ||
			this.ocrFrontPreviewUrl ||
			this.ocrBackPreviewUrl ||
			this.detail.data?.OCR?.images?.temporary?.length ||
			this.detail.data?.POL?.images?.selfie?.length ||
			this.detail.data?.DOCUMENTS?.images?.document?.length ||
			this.debugFallbackImages.length
		);
	}

	/**
	 * Hasta 4 imágenes `images_debug` cuando el proceso no tiene capturas de usuario
	 * (vídeo, OCR, POL, DOCUMENTS) y el usuario puede ver debug (NIF-974). Vacío en otros casos.
	 */
	public get debugFallbackImages(): string[] {
		if (!this.rolePermissions.canViewProcessImagesDebug()) {
			return [];
		}
		const d = this.detail?.data;
		const hasUserMedia = !!(
			(this.detail?.general?.video && this.detail.general.video.trim() !== '') ||
			this.ocrFrontPreviewUrl ||
			this.ocrBackPreviewUrl ||
			d?.OCR?.images?.temporary?.length ||
			d?.POL?.images?.selfie?.length ||
			d?.DOCUMENTS?.images?.document?.length
		);
		if (hasUserMedia) {
			return [];
		}
		return (this.detail?.images_debug ?? [])
			.filter((url): url is string => typeof url === 'string' && url.trim() !== '')
			.slice(0, 4);
	}

	private updateConfigGrid(): void {
		this.gridTemplateColumns = this.computeGridTemplateColumns();
		this.gridMaxWidth = this.computeMaxWidth();
	}

	private computeGridTemplateColumns(): string {
		let gridTemplateColumns = '';
		if (this.detail.general?.video?.trim()) {
			gridTemplateColumns += '1fr';
		}
		if (this.ocrFrontPreviewUrl) {
			gridTemplateColumns += ' 2fr';
		}
		if (this.ocrBackPreviewUrl) {
			gridTemplateColumns += ' 2fr';
		}
		gridTemplateColumns += this.ocrTemporaryGridColumnsSuffix();
		gridTemplateColumns += this.polSelfieGridColumnsSuffix();
		if (this.detail.data?.DOCUMENTS?.images?.document?.length) {
			gridTemplateColumns += ' 1fr';
		}
		for (let i = 0; i < this.debugFallbackImages.length; i++) {
			gridTemplateColumns += ' 1fr';
		}
		return gridTemplateColumns.trim();
	}

	private ocrTemporaryGridColumnsSuffix(): string {
		const ocrImages = this.detail.data?.OCR?.images;
		if (this.ocrFrontPreviewUrl || this.ocrBackPreviewUrl) {
			return '';
		}
		if (ocrImages?.temporary?.length) {
			return ' 2fr';
		}
		return '';
	}

	private polSelfieGridColumnsSuffix(): string {
		const selfieCount = this.polSelfiePreviews.length;
		if (selfieCount > 1) {
			return ' 1fr 1fr';
		}
		if (selfieCount === 1) {
			return ' 1fr';
		}
		return '';
	}

	private computeMaxWidth(): string {
		let maxWidth = 0;
		this.gridTemplateColumns.split(' ').forEach((column) => {
			const fr = parseInt(column.trim().replace('fr', ''), 10);
			maxWidth += fr * this.oneFractionWidth;
		});
		return `${maxWidth}px`;
	}

	public onMediaClick(url: string | undefined): void {
		if (url) {
			this.mediaPreviewClick.emit(url);
		}
	}
}
